/* globals INITIAL_STATE */
import { getVariantCategoryName, mapPayloadForClient } from '../utils/InitializePurchaseUtils';
import deepmerge from 'deepmerge';
import { snakeCaseKeys } from 'config/utils';

const offerData = () => {
  const {
    should_retry: shouldRetry,
  } = INITIAL_STATE;
  const {
    is_link_payment: isLinkPayment,
    is_instapay_payment: isInstapayPayment,
    show_additional_info_tab: hasAdditionalInfoTab,
    offer: {
      variant_categories: variantCategories,
      base_price: basePrice,
      min_price: minPrice,
      shipping_address: isPhysicalGood,
      socialpay_platforms: isSocialPay,
      prefilled,
      slug,
    } = {},
    seller: {
      username,
    } = {},
    discount: {
      discount_amount: discountAmount,
    } = {},
  } = shouldRetry ? INITIAL_STATE.retry_data : INITIAL_STATE;

  // ALl Initial Form props that can affect the totalAmount
  const initialFormFields = {
    discountAmount,
  };

  const prefilledProps = mapPayloadForClient(prefilled);

  return {
    isLinkPayment,
    isInstapayPayment,
    hasAdditionalInfoTab,
    variantCategories,
    basePrice,
    minPrice,
    isPhysicalGood,
    isSocialPay,
    prefilled,
    slug,
    discountAmount,
    prefilledProps,
    initialFormFields,
    username,
  };
};


/**
 * Calculate total amount based on the state of the payment
 */
export function calculateTotalAmount (state) {
  return calculateAmountPayable(getVariableFields(state));
}


/**
 * method that returns all fields that can modify the totalAmount
 * @param  {object} state - to pull out fields from
 * @return {object}
 */
export function getVariableFields (state) {
  const { offer: { basePrice, variantAmountMap, priceBoost }, form } = state;
  return { basePrice, variantAmountMap, form, priceBoost };
}


/**
 * totalAmount calculator
 * @param  {number} basePrice
 * @param  {object} variantAmountMap e.g. {category_5: {5: 199, 6: 499}}
 * @param  {object} form - inputs from the user
 * @return {number} totalAmount
 */
export function calculateAmountPayable ({ basePrice, variantAmountMap = {}, priceBoost, form, prefilled }) {
  if (!prefilled) {
    prefilled = offerData().prefilledProps;
  }

  const signedAmount = prefilled.amount ? Number(prefilled.amount.value) : null;
  let totalAmount = Number(signedAmount ? signedAmount : (
    priceBoost && form.amount >= basePrice ? form.amount : basePrice
  ));

  if (form.discountAmount && !signedAmount) {
    totalAmount = totalAmount - form.discountAmount;
  }

  const amountWithSelectedVariants = Object.keys(variantAmountMap).filter(mapKey => {
    return mapKey in form;
  }).reduce((sum, selectedVariant) => {
    sum += variantAmountMap[selectedVariant][form[selectedVariant]];
    return sum;
  }, totalAmount);

  totalAmount = amountWithSelectedVariants;

  if (form.quantity && !signedAmount) {
    totalAmount = totalAmount * form.quantity;
  }

  if (totalAmount < 0) {
    totalAmount = 0;
  }

  return totalAmount;
}


/**
 * reduces the variant data sent by the server to suit pricing needs
 * @param  {Array} variantCategories - server sent
 * @return {Object} - an object that looks like this -
 *                    {category_5: {'': 0, 5: 199, 6: 499}}
 *                    it adds that blank key for when
 *                    user selects "Select this Variant"
 */
export function getVariantAmountMap (variantCategories = offerData().variantCategories) {
  if (!variantCategories.length) {
    return {};
  }

  return variantCategories.reduce((variantAmountMap, category) => {
    variantAmountMap[getVariantCategoryName(category.id)] = category.variants.reduce((variantMap, variant) => {
      variantMap[variant.id] = Number(variant.amount_added);
      return variantMap;
    }, { '': 0 });
    return variantAmountMap;
  }, {});
}

/**
 * accepts an initial state and to be modified `form` props
 * @param  {object} currentState
 * @param  {object} changedFormData - to be modified form props
 * @return {object} - reduced state
 */
export function reducedState (currentState, changedFormData) {
  const newState = deepmerge(currentState, { form: { ...changedFormData } });
  return {
    ...newState,
  };
}

/**
 * Gets initial state total amount
 * @return {number} - reduced state
 */

export function getInitialStateTotalAmount () {
  const { basePrice, variantAmountMap, initialFormFields, prefilledProps } = offerData();
  return calculateAmountPayable({ basePrice, variantAmountMap, form: initialFormFields, prefilled: prefilledProps });
}

export function getShippingChargesEndPoint (data) {
  const params = (data) ? jsonToQueryString(data) : '';
  const { slug } = offerData();
  if (slug) {
    return `/checkout${slug}shipping-charges${params}`;
  }
}

function jsonToQueryString(json) {
  let snakeCaseJson = snakeCaseKeys(json);
  return '?' +
    Object.keys(snakeCaseJson).map(function(key) {
        return encodeURIComponent(key) + '=' +
            encodeURIComponent(snakeCaseJson[key]);
    }).join('&');
}

export function getOrderEndpoint () {
  const { slug, isLinkPayment, isInstapayPayment } = offerData();
  if (isLinkPayment || isInstapayPayment) {
    return `${location.pathname}`;
  }
  if (slug) {
    return `/checkout${slug}order/`;
  }

}

export function getAccordianHeading (step, totalAmount) {
  const { hasAdditionalInfoTab, isSocialPay, isPhysicalGood, minPrice } = offerData();
  switch (step) {
    case 'InitializePurchase':
      if(totalAmount || minPrice || hasAdditionalInfoTab || isSocialPay) {
        return 'Your Details';
      }
      return 'Claim for Free';

    case 'RefinePurchase':
      if (isPhysicalGood) {
        return 'Shipping';
      }
      return 'Extras';

    case 'FinalizePurchase':
      return 'Payment';
  }
}

export const getCartTotalItems = () =>
  INITIAL_STATE.payment.suborders.length;



// WEBPACK FOOTER //
// ./src/payments/utils/GlobalUtils.js